import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const TONES = {
  blue: "bg-[#EFF6FF] text-[#2563EB] border-[#bfdbfe]",
  cyan: "bg-[#E0F2FE] text-[#0E7490] border-[#bae6fd]",
  green: "bg-[#DCFCE7] text-[#15803D] border-[#bbf7d0]",
  amber: "bg-[#FEF3C7] text-[#B45309] border-[#fde68a]",
  red: "bg-[#FEE2E2] text-[#B91C1C] border-[#fecaca]",
  gray: "bg-[#F1F5F9] text-[#475569] border-[#e2e8f0]",
};
const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function MonthCalendar({ events = [], onEventClick }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  const todayIso = iso(new Date());

  const eventsForDay = (date) => {
    const day = iso(date);
    return events.filter((e) => (e.start || e.date) <= day && (e.end || e.date) >= day);
  };

  return (
    <div className="card p-4 max-w-full overflow-hidden">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold font-head text-[#16324F]">{MONTHS[month]} {year}</h3>
        <div className="flex gap-1">
          <button onClick={() => setCursor(new Date(year, month - 1, 1))} className="btn-outline !p-2" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => setCursor(new Date())} className="btn-outline !py-2 !px-3 text-xs" data-testid="cal-today">Aujourd'hui</button>
          <button onClick={() => setCursor(new Date(year, month + 1, 1))} className="btn-outline !p-2" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="overflow-x-auto -mx-1 px-1">
      <div className="grid grid-cols-7 gap-1 min-w-[560px]">
        {DAYS.map((d) => <div key={d} className="text-center text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] py-1">{d}</div>)}
        {cells.map((date, i) => {
          if (!date) return <div key={i} className="min-h-[64px] sm:min-h-[84px] rounded-lg bg-transparent" />;
          const dayEvents = eventsForDay(date);
          const isToday = iso(date) === todayIso;
          return (
            <div key={i} className={`min-h-[64px] sm:min-h-[84px] rounded-lg border p-1.5 ${isToday ? "border-[#2563EB] bg-[#EFF6FF]/40" : "border-[#F1F5F9] bg-white"}`}>
              <div className={`text-xs font-semibold mb-1 ${isToday ? "text-[#2563EB]" : "text-[#64748B]"}`}>{date.getDate()}</div>
              <div className="space-y-1">
                {dayEvents.slice(0, 3).map((e, j) => (
                  <button key={j} onClick={() => onEventClick && onEventClick(e)} title={e.title} data-testid="cal-event"
                    className={`block w-full text-left truncate text-[10px] font-medium px-1.5 py-0.5 rounded border ${TONES[e.tone] || TONES.gray}`}>
                    {e.title}
                  </button>
                ))}
                {dayEvents.length > 3 && <div className="text-[10px] text-[#94A3B8] px-1">+{dayEvents.length - 3} autre(s)</div>}
              </div>
            </div>
          );
        })}
      </div>
      </div>
    </div>
  );
}
